import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import RatingModal from '../components/RatingModal';
import './Notifications.css';

/* ─────────────────────────────────────────────────────────────
   NOTIFICATIONS PAGE
   Layout:
     • Pending   — actionable, not yet responded to (top)
     • Resolved  — acted on or informational (collapsible, bottom)
   ───────────────────────────────────────────────────────────── */

/* ── Icons ──────────────────────────────────────────────────── */
function PersonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

function PaddleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="10" r="6"/>
      <line x1="12" y1="16" x2="12" y2="22"/>
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4a2 2 0 0 1-2-2V5h4"/>
      <path d="M18 9h2a2 2 0 0 0 2-2V5h-4"/>
      <path d="M6 9a6 6 0 0 0 12 0"/>
      <line x1="12" y1="15" x2="12" y2="19"/>
      <line x1="8" y1="19" x2="16" y2="19"/>
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
    >
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

/* ── Helpers ────────────────────────────────────────────────── */
function timeAgo(dateStr) {
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  < 7)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* A notification is "pending" if it needs an action and hasn't had one yet */
const ACTIONABLE = new Set(['friend_request', 'match_confirmation', 'tournament_completed']);
function isPending(n) {
  return ACTIONABLE.has(n.type) && !n.actioned;
}

/* ── Main component ─────────────────────────────────────────── */
export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [resolvedOpen,  setResolvedOpen]  = useState(false);

  // Placement gate
  const [isRated,           setIsRated]           = useState(true);
  const [ratingModalOpen,   setRatingModalOpen]   = useState(false);
  const [pendingMatchNotif, setPendingMatchNotif] = useState(null);

  useEffect(() => {
    if (user) fetchAndMarkRead();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAndMarkRead() {
    setLoading(true);

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_rated')
      .eq('id', user.id)
      .single();
    setIsRated(profile?.is_rated ?? false);

    const { data: notifs } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    const notifData = notifs || [];

    // Batch-load usernames for related_user_id
    const relatedIds = [...new Set(notifData.map(n => n.related_user_id).filter(Boolean))];
    let usernameMap = {};
    if (relatedIds.length) {
      const { data: profiles } = await supabase
        .from('profiles').select('id, username').in('id', relatedIds);
      (profiles || []).forEach(p => { usernameMap[p.id] = p.username; });
    }

    setNotifications(notifData.map(n => ({ ...n, related_username: usernameMap[n.related_user_id] || null })));
    setLoading(false);

    // Mark all unread as read
    const unreadIds = notifData.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length) {
      await supabase.from('notifications').update({ is_read: true }).in('id', unreadIds);
      window.dispatchEvent(new CustomEvent('ranktt:notificationsRead'));
    }
  }

  /* Sets actioned on the DB row and does a local optimistic update */
  async function setActioned(notifId, value) {
    await supabase.from('notifications').update({ actioned: value }).eq('id', notifId);
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, actioned: value } : n));
  }

  async function handleFriendRespond(notif, accept) {
    await supabase.rpc('respond_to_friend_request', {
      p_friendship_id: notif.related_id,
      p_accept:        accept,
    });
    await setActioned(notif.id, accept ? 'accepted' : 'declined');
    window.dispatchEvent(new CustomEvent('ranktt:friendResolved'));
  }

  async function handleMatchRespond(notif, accept) {
    if (accept && !isRated) {
      setPendingMatchNotif(notif);
      setRatingModalOpen(true);
      return;
    }
    const { error } = await supabase.rpc('confirm_match', {
      p_match_id: notif.related_id,
      p_accept:   accept,
    });
    if (error) { alert('Error: ' + error.message); return; }
    await setActioned(notif.id, accept ? 'confirmed' : 'declined');
    window.dispatchEvent(new CustomEvent('ranktt:matchResolved'));
  }

  async function handleTournamentRespond(notif, confirmed) {
    await setActioned(notif.id, confirmed ? 'confirmed' : 'declined');
  }

  /* ── Notification row ─────────────────────────────────────── */
  function NotifRow({ notif, dimmed }) {
    const icon =
      notif.type === 'friend_request'      ? <PersonIcon /> :
      notif.type === 'tournament_completed' ? <TrophyIcon /> :
      <PaddleIcon />;

    const actionLabel = notif.actioned === 'confirmed' || notif.actioned === 'accepted'
      ? { label: notif.actioned === 'confirmed' ? 'Confirmed ✓' : 'Accepted ✓', cls: 'notif-actioned--yes' }
      : notif.actioned
      ? { label: 'Declined', cls: 'notif-actioned--no' }
      : null;

    return (
      <div className={`notif-row ${dimmed ? 'notif-row--dimmed' : ''}`}>
        <div className="notif-icon">{icon}</div>

        <div className="notif-body">
          <p className="notif-content">{notif.content}</p>
          <span className="notif-time">{timeAgo(notif.created_at)}</span>
          {notif.type === 'friend_request' && notif.related_username && (
            <Link to={`/profile/${notif.related_username}`} className="notif-profile-link">
              View profile →
            </Link>
          )}
          {notif.type === 'tournament_completed' && notif.related_id && (
            <Link to={`/tournaments/${notif.related_id}`} className="notif-profile-link">
              View tournament →
            </Link>
          )}
        </div>

        <div className="notif-actions">
          {/* Show actioned badge if already resolved */}
          {actionLabel && (
            <span className={`notif-actioned ${actionLabel.cls}`}>{actionLabel.label}</span>
          )}

          {/* Pending action buttons */}
          {!notif.actioned && notif.type === 'friend_request' && (
            <>
              <button className="notif-btn notif-btn--accept" onClick={() => handleFriendRespond(notif, true)}>Accept</button>
              <button className="notif-btn notif-btn--decline" onClick={() => handleFriendRespond(notif, false)}>Decline</button>
            </>
          )}
          {!notif.actioned && notif.type === 'match_confirmation' && (
            <>
              <button className="notif-btn notif-btn--accept" onClick={() => handleMatchRespond(notif, true)}>Confirm</button>
              <button className="notif-btn notif-btn--decline" onClick={() => handleMatchRespond(notif, false)}>Decline</button>
            </>
          )}
          {!notif.actioned && notif.type === 'tournament_completed' && (
            <>
              <button className="notif-btn notif-btn--accept" onClick={() => handleTournamentRespond(notif, true)}>Confirm</button>
              <button className="notif-btn notif-btn--decline" onClick={() => handleTournamentRespond(notif, false)}>Decline</button>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── Loading ─────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="notif-page">
        <div className="notif-loading">
        <span className="notif-loading-star">★</span>
        <span className="notif-loading-star">★</span>
        <span className="notif-loading-star">★</span>
      </div>
      </div>
    );
  }

  /* ── Split into pending / resolved ──────────────────────── */
  const pending  = notifications.filter(n =>  isPending(n));
  const resolved = notifications.filter(n => !isPending(n));

  return (
    <div className="notif-page">
      <h1 className="notif-title">Notifications</h1>

      {/* ── Pending section ─────────────────────────────────── */}
      {pending.length === 0 ? (
        <div className="notif-all-clear">
          <span className="notif-all-clear-icon">✓</span>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="notif-list">
          {pending.map(n => <NotifRow key={n.id} notif={n} dimmed={false} />)}
        </div>
      )}

      {/* ── Resolved section (collapsible) ──────────────────── */}
      {resolved.length > 0 && (
        <div className="notif-resolved-section">
          <button
            className="notif-resolved-toggle"
            onClick={() => setResolvedOpen(o => !o)}
          >
            <span>Resolved</span>
            <span className="notif-resolved-count">{resolved.length}</span>
            <ChevronIcon open={resolvedOpen} />
          </button>

          {resolvedOpen && (
            <div className="notif-list notif-list--resolved">
              {resolved.map(n => <NotifRow key={n.id} notif={n} dimmed={true} />)}
            </div>
          )}
        </div>
      )}

      {/* Placement gate */}
      <RatingModal
        isOpen={ratingModalOpen}
        onClose={() => { setRatingModalOpen(false); setPendingMatchNotif(null); }}
        userId={user?.id}
        onRated={() => {
          setIsRated(true);
          setRatingModalOpen(false);
          if (pendingMatchNotif) handleMatchRespond(pendingMatchNotif, true);
          setPendingMatchNotif(null);
        }}
      />
    </div>
  );
}
